import { create } from 'zustand'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

export type FeedbackCategory = 'bug' | 'feature-request' | 'improvement' | 'question'

export interface FeedbackScreenshot {
  base64: string
  mimeType: string
}

interface SubmitFeedbackInput {
  category: FeedbackCategory
  message: string
  screenshots?: FeedbackScreenshot[]
  pageUrl?: string
  userAgent?: string
  metadata?: Record<string, unknown>
}

interface FeedbackResponse {
  feedback: {
    id: string
    category: FeedbackCategory
    message: string
    createdAt: string
  }
}

// ===================
// Store
// ===================

interface FeedbackState {
  isOpen: boolean
  isSubmitting: boolean
  error: string | null

  open: () => void
  close: () => void
  submitFeedback: (input: SubmitFeedbackInput) => Promise<boolean>
}

export const useFeedbackStore = create<FeedbackState>()((set) => ({
  isOpen: false,
  isSubmitting: false,
  error: null,

  open: () => set({ isOpen: true, error: null }),
  close: () => set({ isOpen: false, error: null, isSubmitting: false }),

  submitFeedback: async (input: SubmitFeedbackInput): Promise<boolean> => {
    set({ isSubmitting: true, error: null })
    try {
      await api.post<FeedbackResponse>('/feedback', input)
      set({ isSubmitting: false, isOpen: false })
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit feedback'
      set({ isSubmitting: false, error: message })
      return false
    }
  },
}))
